import { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';

function Members() {
  const [vmSettings, setVmSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedNickname, setEditedNickname] = useState('');
  const [activeVM, setActiveVM] = useState(null);
  const [selectedLab, setSelectedLab] = useState(0);
  const [isLabCollapsed, setIsLabCollapsed] = useState(false);
  const [labs, setLabs] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [textSize, setTextSize] = useState('medium');
  const [userRating, setUserRating] = useState(0);
  const [savedRating, setSavedRating] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
    ],
    content: '',
    editorProps: {
      handlePaste: () => false, // Disable default paste handling
    },
    onPaste: ({ editor, event }) => {
      event.preventDefault();
      const text = event.clipboardData?.getData('text/plain');
      if (text) {
        editor.commands.insertContent(text);
      }
    },
  });

  useEffect(() => {
    fetchVMSettings();
    fetchLabs();
  }, []);

  const fetchVMSettings = async () => {
    try {
      const docRef = doc(db, 'training-config', 'student-settings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Store all server groups
        setVmSettings({
          serverGroups: data.serverGroups || []
        });
      } else {
        setError('No VM settings found');
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch VM settings: ' + err.message);
      setLoading(false);
    }
  };

  const fetchLabs = async () => {
    try {
      const docRef = doc(db, 'training-config', 'labs');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setLabs(docSnap.data().labs);
      } else {
        const defaultLabs = Array(3).fill(null).map((_, index) => ({
          title: `Lab ${index + 1}`,
          steps: [{
            step: 1,
            instruction: `This is step 1 of Lab ${index + 1}. Lorem ipsum dolor sit amet, consectetur adipiscing elit.`
          }]
        }));
        await setDoc(doc(db, 'training-config', 'labs'), { labs: defaultLabs });
        setLabs(defaultLabs);
      }
    } catch (err) {
      console.error('Error fetching labs:', err);
      setError('Failed to fetch labs: ' + err.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleConnectVM = (connectionUrl) => {
    // Add credentials as a query parameter according to Guacamole spec
    const separator = connectionUrl.includes('?') ? '&' : '?';
    const urlWithAuth = `${connectionUrl}${separator}credentials=student:student123`;
    
    // Log the connection URL for debugging (optional)
    console.log('Connecting to:', urlWithAuth);
    
    // Open in new tab
    window.open(urlWithAuth, '_blank');
  };

  const handleBackToList = () => {
    setActiveVM(null);
  };

  const handleStepEdit = async (stepIndex, newContent) => {
    try {
      const updatedLabs = [...labs];
      updatedLabs[selectedLab].steps[stepIndex].instruction = newContent;
      
      await setDoc(doc(db, 'training-config', 'labs'), {
        labs: updatedLabs
      });
      
      setLabs(updatedLabs);
      setEditingStep(null);
    } catch (error) {
      console.error('Error saving lab content:', error);
      alert('Failed to save lab content. Please try again.');
    }
  };

  const handleDoneEditing = async () => {
    if (editingStep !== null && editor) {
      const content = editor.getHTML();
      await handleStepEdit(editingStep, content);
    }
  };

  const startEditing = (index, content) => {
    setEditingStep(index);
    // Add a small delay to ensure the editor is ready
    setTimeout(() => {
      editor?.commands.setContent(content);
    }, 0);
  };

  const handleCancelEditing = () => {
    setEditingStep(null);
  };

  const handleSaveNickname = async (index) => {
    try {
      // Get the current document
      const docRef = doc(db, 'training-config', 'student-settings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Get the first group (Group A) from serverGroups
        const firstGroup = data.serverGroups?.[0];
        if (firstGroup) {
          // Create a copy of the serverGroups array
          const updatedServerGroups = [...data.serverGroups];
          // Update the nickname in the first group's vmConnections
          updatedServerGroups[0] = {
            ...firstGroup,
            vmConnections: firstGroup.vmConnections.map((vm, i) => 
              i === index ? { ...vm, nickname: editedNickname } : vm
            )
          };

          // Update the document with the new serverGroups
          await setDoc(docRef, {
            ...data,
            serverGroups: updatedServerGroups
          });

          // Update local state
          setVmSettings({
            serverGroups: updatedServerGroups
          });
          setEditingIndex(null);
        }
      }
    } catch (error) {
      console.error('Error updating nickname:', error);
    }
  };

  const isAuthorizedEditor = () => {
    return auth.currentUser?.email === 'timcoakley@yahoo.co.uk';
  };

  const refreshVMSettings = async () => {
    try {
      const docRef = doc(db, 'training-config', 'student-settings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setVmSettings({
          serverGroups: data.serverGroups || []
        });
      }
    } catch (err) {
      console.error('Error refreshing settings:', err);
      setError('Failed to refresh VM settings: ' + err.message);
    }
  };

  const handleRating = async (rating) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      // Use userId as the document ID
      const userRatingDoc = doc(db, 'user-reviews', userId);
      
      // Update or create the document with the new rating
      await setDoc(userRatingDoc, {
        [`lab${selectedLab + 1}rating`]: rating,
        updatedAt: new Date()
      }, { merge: true }); // merge: true will only update the specified fields

      setUserRating(rating);
      setSavedRating(rating);
    } catch (error) {
      console.error('Error saving rating:', error);
    }
  };

  useEffect(() => {
    const fetchUserRating = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        // Get the user's ratings document
        const userRatingDoc = doc(db, 'user-reviews', userId);
        const docSnap = await getDoc(userRatingDoc);

        if (docSnap.exists()) {
          const rating = docSnap.data()[`lab${selectedLab + 1}rating`] || 0;
          setUserRating(rating);
          setSavedRating(rating);
        } else {
          setUserRating(0);
          setSavedRating(0);
        }
      } catch (error) {
        console.error('Error fetching rating:', error);
      }
    };

    fetchUserRating();
  }, [selectedLab]);

  // Add this new component for the formatting toolbar
  const MenuBar = ({ editor }) => {
    if (!editor) {
      return null;
    }

    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '10px',
        padding: '8px',
        background: '#333',
        borderRadius: '4px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('bold') ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Bold
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('italic') ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Italic
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('strike') ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Strike
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={editor.isActive('code') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('code') ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Code
        </button>
        <button
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={editor.isActive('paragraph') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('paragraph') ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Paragraph
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('heading', { level: 2 }) ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Heading
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('bulletList') ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Bullet List
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          style={{
            padding: '4px 8px',
            fontSize: '0.8rem',
            background: editor.isActive('orderedList') ? '#666' : '#444',
            border: 'none',
            borderRadius: '4px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Ordered List
        </button>
        <div style={{ 
          borderLeft: '1px solid #555',
          marginLeft: '4px',
          paddingLeft: '4px',
          display: 'flex',
          gap: '8px'
        }}>
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
            style={{
              padding: '4px 8px',
              fontSize: '0.8rem',
              background: editor.isActive({ textAlign: 'left' }) ? '#666' : '#444',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Left
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
            style={{
              padding: '4px 8px',
              fontSize: '0.8rem',
              background: editor.isActive({ textAlign: 'center' }) ? '#666' : '#444',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Center
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
            style={{
              padding: '4px 8px',
              fontSize: '0.8rem',
              background: editor.isActive({ textAlign: 'right' }) ? '#666' : '#444',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Right
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
            style={{
              padding: '4px 8px',
              fontSize: '0.8rem',
              background: editor.isActive({ textAlign: 'justify' }) ? '#666' : '#444',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Justify
          </button>
        </div>
      </div>
    );
  };

  // Add this function to get the font size based on selection
  const getFontSize = () => {
    switch(textSize) {
      case 'small': return '0.9rem';
      case 'large': return '1.2rem';
      default: return '1rem';
    }
  };

  if (loading) return (
    <div style={{
      height: '100vh',
      width: '100vw',
      backgroundColor: '#1a1a1a',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'fixed',
      top: 0,
      left: 0
    }}>
      <div className="loader" style={{
        width: '50px',
        height: '50px',
        border: '5px solid #333',
        borderTop: '5px solid #fff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        code {
          background-color: #383838;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          display: inline-block;
          margin: 0 2px;
        }
      `}</style>
    </div>
  );

  if (error) return <div>Error: {error}</div>;

  return (
    <div className="App" style={{ height: '100vh', overflow: 'hidden' }}>
      <style>{`
        code {
          background-color: #383838;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          display: inline-block;
          margin: 0 2px;
        }
      `}</style>
      <header className="App-header" style={{ height: '100%', overflow: 'hidden' }}>
        <h1 style={{ fontSize: '1.8rem' }}>Training Portal</h1>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <p style={{ fontSize: '1rem' }}>Welcome, {auth.currentUser?.email}!</p>
            <button
              onClick={() => window.location.href = 'https://aisectraining.com/earlybirddiscount'}
              style={{
                background: 'transparent',
                border: '2px solid #87CEEB',
                borderRadius: '8px',
                padding: '8px 16px',
                color: '#87CEEB',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                marginTop: '10px',
                ':hover': {
                  background: 'rgba(135, 206, 235, 0.1)'
                }
              }}
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="16" y1="11" x2="22" y2="11" />
              </svg>
              <strong>AI Security for Cybersecurity Professionals</strong> <br />
              Register Your Interest Now
            </button>
          </div>
        </div>
        
        <div style={{ display: 'flex', height: 'calc(100% - 100px)', overflow: 'hidden' }}>
          {/* Left side - VM List */}
          <div style={{ 
            width: '300px',
            minWidth: '300px',
            padding: '0 20px 20px 0',
            borderRight: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}>
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              marginBottom: '20px',
              width: '100%'
            }}>
              {vmSettings && (
                <div className="vm-container" style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '-8px'
                  }}>
                    <h4 style={{ 
                      fontSize: '1rem', 
                      color: '#87CEEB',
                      margin: 0
                    }}>Virtual Machines</h4>
                    <button
                      onClick={refreshVMSettings}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#87CEEB',
                        opacity: '0.8',
                        transition: 'opacity 0.2s',
                        ':hover': {
                          opacity: '1'
                        }
                      }}
                      title="Refresh VM list"
                    >
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                      </svg>
                    </button>
                  </div>

                  <div className="connection-instructions" style={{
                    background: '#2a2a2a',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '10px'
                  }}>
                    <h4 style={{ 
                      fontSize: '1rem', 
                      marginBottom: '10px',
                      color: '#87CEEB' 
                    }}>Connection Instructions:</h4>
                    <ol style={{ 
                      paddingLeft: '20px',
                      fontSize: '0.9rem',
                      color: '#e0e0e0',
                      lineHeight: '1.4'
                    }}>
                      <li>Click the "Connect to VM" button for your desired machine</li>
                      <li>The VM interface will appear on the right</li>
                      <li>You will be automatically logged in to the VM</li>
                      <li>You can use the VM directly in your browser - no additional software needed!</li>
                    </ol>
                  </div>

                  {vmSettings && vmSettings.serverGroups && (
                    <div style={{ marginBottom: '15px' }}>
                      <select
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(Number(e.target.value))}
                        style={{
                          padding: '8px',
                          width: '100%',
                          background: '#333',
                          color: 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          fontSize: '0.9rem'
                        }}
                      >
                        {vmSettings.serverGroups.map((group, index) => (
                          <option key={index} value={index}>
                            {group.name || `Group ${String.fromCharCode(65 + index)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="vm-list" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    {vmSettings && vmSettings.serverGroups && vmSettings.serverGroups[selectedGroup]?.vmConnections.map((vm, index) => (
                      <div key={index} style={{
                        background: '#2a2a2a',
                        borderRadius: '8px',
                        padding: '15px',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        {editingIndex === index ? (
                          <div className="edit-form" style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center'
                          }}>
                            <input
                              type="text"
                              value={editedNickname}
                              onChange={(e) => setEditedNickname(e.target.value)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #4B89DC',
                                background: '#1a1a1a',
                                color: 'white'
                              }}
                            />
                            <button 
                              onClick={() => handleSaveNickname(index)}
                              style={{
                                padding: '8px 16px',
                                background: '#4CAF50',
                                border: 'none',
                                borderRadius: '4px',
                                color: 'white',
                                cursor: 'pointer'
                              }}
                            >
                              Save
                            </button>
                            <button 
                              onClick={() => setEditingIndex(null)}
                              style={{
                                padding: '8px 16px',
                                background: '#666',
                                border: 'none',
                                borderRadius: '4px',
                                color: 'white',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px'
                            }}>
                              <span style={{
                                fontSize: '1.1rem',
                                fontWeight: '500',
                                color: '#87CEEB'
                              }}>
                                {vm.nickname}
                              </span>
                              <button 
                                onClick={() => {
                                  setEditingIndex(index);
                                  setEditedNickname(vm.nickname);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  background: 'transparent',
                                  border: '1px solid #87CEEB',
                                  borderRadius: '4px',
                                  color: '#87CEEB',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  alignSelf: 'flex-start'
                                }}
                              >
                                Edit Name
                              </button>
                            </div>
                            <button 
                              className="connect-btn"
                              onClick={() => handleConnectVM(vm.connection)}
                              style={{
                                padding: '12px',
                                background: 'linear-gradient(to right, #4B89DC, #3498db)',
                                border: 'none',
                                borderRadius: '6px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: '500',
                                transition: 'transform 0.2s',
                                ':hover': {
                                  transform: 'translateY(-1px)'
                                }
                              }}
                            >
                              Connect to VM
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Logout button in fixed position at bottom */}
            <div style={{ flexShrink: 0 }}>
              <button 
                className="logout-btn" 
                onClick={handleLogout}
                style={{ 
                  fontSize: '0.9rem', 
                  padding: '8px 16px',
                  width: '100%'
                }}
              >
                Logout
              </button>
            </div>
          </div>

          {/* Right side - Lab Instructions */}
          <div style={{ 
            flex: 1,
            background: '#2a2a2a',
            padding: '20px',
            position: 'relative',
            overflowX: 'hidden'
          }}>
            {!isLabCollapsed && (
              <div style={{ 
                height: '100%', 
                overflowY: 'auto',
                maxWidth: '1000px',
                margin: '0 auto',
                padding: '0 20px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '20px', 
                  marginBottom: '20px',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <select 
                    value={selectedLab}
                    onChange={(e) => setSelectedLab(Number(e.target.value))}
                    style={{
                      padding: '8px',
                      width: '200px',
                      background: '#333',
                      color: 'white',
                      border: '1px solid #444',
                      borderRadius: '4px'
                    }}
                  >
                    {labs.map((lab, index) => (
                      <option key={index} value={index}>{lab.title}</option>
                    ))}
                  </select>

                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '0.9rem', marginRight: '4px' }}>Text Size:</span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => setTextSize('small')}
                        style={{
                          padding: '4px 8px',
                          background: textSize === 'small' ? '#666' : '#333',
                          color: 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          minWidth: '30px'
                        }}
                      >
                        A
                      </button>
                      <button
                        onClick={() => setTextSize('medium')}
                        style={{
                          padding: '4px 8px',
                          background: textSize === 'medium' ? '#666' : '#333',
                          color: 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          minWidth: '30px'
                        }}
                      >
                        A
                      </button>
                      <button
                        onClick={() => setTextSize('large')}
                        style={{
                          padding: '4px 8px',
                          background: textSize === 'large' ? '#666' : '#333',
                          color: 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                          minWidth: '30px'
                        }}
                      >
                        A
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ 
                  marginTop: '20px',
                  maxWidth: '900px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '20px'
                  }}>
                    <h3>{labs[selectedLab].title}</h3>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px'
                    }}>
                      <span style={{ fontSize: '0.9rem' }}>Rate this lab:</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => handleRating(star)}
                            onMouseEnter={() => setUserRating(star)}
                            onMouseLeave={() => setUserRating(savedRating)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px',
                              color: star <= userRating ? '#ffd700' : '#666'
                            }}
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill={star <= userRating ? 'currentColor' : 'none'}
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {labs[selectedLab].steps.map((step, index) => (
                    <div 
                      key={index}
                      style={{
                        padding: '10px',
                        marginBottom: '10px',
                        background: '#333',
                        borderRadius: '4px'
                      }}
                    >
                      <strong>Instructions:</strong>
                      <div 
                        style={{ 
                          marginTop: '5px',
                          background: '#2a2a2a',
                          borderRadius: '4px',
                          padding: '10px'
                        }}
                      >
                        <div>
                          <div 
                            dangerouslySetInnerHTML={{ __html: step.instruction }}
                            style={{ 
                              marginBottom: '10px',
                              fontSize: getFontSize()
                            }}
                          />
                          {isAuthorizedEditor() && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {editingStep === index ? (
                                <div>
                                  <MenuBar editor={editor} />
                                  <EditorContent 
                                    editor={editor}
                                    style={{
                                      background: '#1a1a1a',
                                      color: 'white',
                                      borderRadius: '4px',
                                      padding: '10px',
                                      minHeight: '100px'
                                    }}
                                  />
                                  <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                                    <button
                                      onClick={handleDoneEditing}
                                      style={{
                                        padding: '4px 8px',
                                        fontSize: '0.8rem',
                                        background: '#4CAF50',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: 'white',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Save Changes
                                    </button>
                                    <button
                                      onClick={handleCancelEditing}
                                      style={{
                                        padding: '4px 8px',
                                        fontSize: '0.8rem',
                                        background: '#666',
                                        border: 'none',
                                        borderRadius: '4px',
                                        color: 'white',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEditing(index, step.instruction)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.8rem',
                                    background: '#444',
                                    border: 'none',
                                    borderRadius: '4px',
                                    color: 'white',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Edit Content
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </div>
  );
}

export default Members; 